/**
 * @fileoverview New User Onboarding Provider
 * 
 * Wraps the app and shows the onboarding modal for new users
 * who haven't set their country and gender yet.
 * After profile setup, shows the post-signup flow (notifications + safari extension).
 */

import { ReactNode, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { NewUserOnboardingModal } from '@/components/onboarding/NewUserOnboardingModal';
import { PostSignupFlow } from '@/components/onboarding/PostSignupFlow';
import { useNewUserOnboarding } from '@/hooks/useNewUserOnboarding';
import { useDevMode } from '@/contexts/DevModeContext';

interface NewUserOnboardingProviderProps {
  children: ReactNode;
}

export function NewUserOnboardingProvider({ children }: NewUserOnboardingProviderProps) {
  const { needsOnboarding, completeOnboarding } = useNewUserOnboarding();
  const [showPostSignup, setShowPostSignup] = useState(false);
  const navigate = useNavigate();
  const { isDevMode } = useDevMode();

  const handleProfileComplete = useCallback(async (data: Parameters<typeof completeOnboarding>[0]) => {
    await completeOnboarding(data);
    setShowPostSignup(true);
  }, [completeOnboarding]);

  const handlePostSignupComplete = useCallback(() => {
    setShowPostSignup(false);
  }, []);

  const handleNavigate = useCallback((path: string) => {
    navigate(path);
  }, [navigate]);

  return (
    <>
      {children}
      <NewUserOnboardingModal 
        open={needsOnboarding} 
        onComplete={handleProfileComplete}
      />
      {showPostSignup && (
        <motion.div
          className="fixed inset-0 z-[60] bg-background"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <PostSignupFlow 
            onComplete={handlePostSignupComplete}
            devMode={isDevMode}
            onNavigate={handleNavigate}
          />
        </motion.div>
      )}
    </>
  );
}
